import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { toast } from "react-toastify";
import {
  addQualityAttributeValueMarketAssignments,
  getQualityAttributeValueMarketAssignments,
} from "../../../common/services/markets/qualityAttributeValueMarketAssignmentService";
import DualListBox from "../../../common/components/form/dualListBox";
import Loading from "../../../common/components/loading/loading";
import { useTranslation } from "react-i18next";
import uuid from "react-uuid";
import {
  formatDateToISOString,
  getTodayYYYYMMDD,
} from "../../../common/utils/dateUtils";
import MarketQualityAttributeAssignmentInputHandler from "./marketQualityAttributeAssignmentInputHandler";
import MarketQualityAttributeAssignmentInputGrid from "./marketQualityAttributeAssignmentInputGrid";
import {
  buildNoEndDateFlags,
  buildOriginalValues,
  buildSelectedQualityAttributeValues,
  mapAssignmentToSelectedValue,
  normalizeAssignmentDate,
} from "./marketQualityAttributeAssignmentInputUtils";
import { getProductWithQualityAttributes } from "../../../common/services/products/productService";

// Constants
const FIELD_START_DATE = "startDate";
const FIELD_END_DATE = "endDate";

const MarketQualityAttributeAssignmentInput = ({
  isNew,
  assignment,
  marketId,
  productId,
  allowEdit,
  submitCallbackFunction,
  onClose,
}) => {
  const { t } = useTranslation(["markets"]);
  const [isLoading, setIsLoading] = useState(true);
  const [availableQualityAttributeValues, setAvailableQualityAttributeValues] =
    useState([]);
  const [
    selectedQualityAttributeValueIds,
    setSelectedQualityAttributeValueIds,
  ] = useState([]);
  const [selectedQualityAttributeValues, setSelectedQualityAttributeValues] =
    useState([]);
  // Date configuration state
  const [applyStartDateToAll, setApplyStartDateToAll] = useState(false);
  const [applyEndDateToAll, setApplyEndDateToAll] = useState(false);
  const [globalStartDate, setGlobalStartDate] = useState("");
  const [globalEndDate, setGlobalEndDate] = useState("");
  const [globalNoEndDate, setGlobalNoEndDate] = useState(false);
  const [noEndDateFlags, setNoEndDateFlags] = useState({}); // Map of qualityAttributeValueId -> boolean
  // Consolidated original values map: { [qualityAttributeValueId]: { startDate, endDate, noEndDate } }
  const [originalValues, setOriginalValues] = useState({});
  const existingAssignmentValuesRef = useRef([]);

  // Consolidated ref to access current state values in callbacks
  const stateRef = useRef({
    availableQualityAttributeValues,
    selectedQualityAttributeValues,
    noEndDateFlags,
  });
  useEffect(() => {
    stateRef.current = {
      availableQualityAttributeValues,
      selectedQualityAttributeValues,
      noEndDateFlags,
    };
  }, [
    availableQualityAttributeValues,
    selectedQualityAttributeValues,
    noEndDateFlags,
  ]);

  useEffect(() => {
    const loadData = async () => {
      try {
        // Load available quality attribute values
        const product = await getProductWithQualityAttributes(productId);
        const allValues = product?.qualityAttributeValues ?? [];

        if (isNew) {
          if (marketId) {
            try {
              const existingAssignments =
                await getQualityAttributeValueMarketAssignments(marketId);

              const today = getTodayYYYYMMDD();

              const activeAssignments = existingAssignments.filter((a) => {
                const endDate = normalizeAssignmentDate(a.endDate);
                return !endDate || endDate >= today;
              });

              const activeSelectedValues = activeAssignments.map(
                mapAssignmentToSelectedValue
              );
              const activeAssignmentIds = activeSelectedValues.map(
                (a) => a.qualityAttributeValueId
              );

              existingAssignmentValuesRef.current = activeSelectedValues;
              setSelectedQualityAttributeValues(activeSelectedValues);
              setSelectedQualityAttributeValueIds(activeAssignmentIds);
              setOriginalValues(buildOriginalValues(activeSelectedValues));
              setNoEndDateFlags(buildNoEndDateFlags(activeSelectedValues));
            } catch (error) {
              console.error("Error loading existing assignments:", error);
              // Continue with all values if we can't fetch existing assignments
            }
          }

          setAvailableQualityAttributeValues(allValues);
        } else {
          // For update mode, just load all values
          setAvailableQualityAttributeValues(allValues || []);
        }
        setIsLoading(false);
      } catch (error) {
        console.error("Error loading quality attribute values:", error);
        setIsLoading(false);
      }
    };

    loadData();
  }, [isNew, marketId, productId]);

  const handleDualListBoxChange = useCallback(
    (selected, selection, controlKey) => {
      const today = getTodayYYYYMMDD();

      // Update selected IDs
      setSelectedQualityAttributeValueIds(selected);

      // Automatically create selected values and show grid when items are selected
      if (selected.length > 0) {
        const selectedValues = buildSelectedQualityAttributeValues({
          selectedIds: selected,
          availableQualityAttributeValues:
            stateRef.current.availableQualityAttributeValues,
          currentSelectedValues: stateRef.current.selectedQualityAttributeValues,
          existingAssignmentValues: existingAssignmentValuesRef.current,
          today,
        });

        // Initialize noEndDateFlags for new selections
        setNoEndDateFlags((prevFlags) => {
          const newNoEndDateFlags = { ...prevFlags };
          selectedValues.forEach((value) => {
            if (!(value.qualityAttributeValueId in newNoEndDateFlags)) {
              newNoEndDateFlags[value.qualityAttributeValueId] = false;
            }
          });
          return newNoEndDateFlags;
        });

        // Initialize consolidated original values map for new selections
        setOriginalValues((prevValues) => {
          const newValues = { ...prevValues };
          selectedValues.forEach((value) => {
            if (!(value.qualityAttributeValueId in newValues)) {
              newValues[value.qualityAttributeValueId] = {
                startDate: value.startDate || "",
                endDate: value.endDate || null,
                noEndDate: false,
              };
            }
          });
          return newValues;
        });

        setSelectedQualityAttributeValues(selectedValues);
      } else {
        // Clear grid when no items selected
        setSelectedQualityAttributeValues([]);
      }
    },
    []
  );

  const handleNoEndDateChange = useCallback(
    (qualityAttributeValueId, checked) => {
      setSelectedQualityAttributeValues((prevValues) => {
        const updated = [...prevValues];
        const index = updated.findIndex(
          (item) => item.qualityAttributeValueId === qualityAttributeValueId
        );

        if (index > -1) {
          // When checking "No End Date", set endDate to null
          // When unchecking, restore the original endDate for this specific row
          const originalValue = originalValues[qualityAttributeValueId];
          const newEndDate = checked
            ? null
            : originalValue?.endDate !== undefined
            ? originalValue.endDate
            : "";
          updated[index].endDate = newEndDate;
          setNoEndDateFlags((prevFlags) => {
            const updatedFlags = { ...prevFlags };
            updatedFlags[qualityAttributeValueId] = checked;
            return updatedFlags;
          });
          // Update original values map when editing individual cell (apply to all is off)
          if (!applyEndDateToAll) {
            setOriginalValues((prevValues) => ({
              ...prevValues,
              [qualityAttributeValueId]: {
                ...(prevValues[qualityAttributeValueId] || {}),
                endDate: newEndDate,
                noEndDate: checked,
              },
            }));
          }
          return updated;
        }
        return prevValues;
      });
    },
    [applyEndDateToAll, originalValues]
  );

  const handleClearEndDate = useCallback(
    (qualityAttributeValueId) => {
      setSelectedQualityAttributeValues((prevValues) => {
        const updated = [...prevValues];
        const index = updated.findIndex(
          (item) => item.qualityAttributeValueId === qualityAttributeValueId
        );

        if (index > -1) {
          // Clear the end date
          updated[index].endDate = null;
          // Clear the noEndDateFlags flag so the field can be edited again
          setNoEndDateFlags((prevFlags) => {
            const updatedFlags = { ...prevFlags };
            updatedFlags[qualityAttributeValueId] = false;
            return updatedFlags;
          });
          // Update original values map when editing individual cell (apply to all is off)
          if (!applyEndDateToAll) {
            setOriginalValues((prevValues) => ({
              ...prevValues,
              [qualityAttributeValueId]: {
                ...(prevValues[qualityAttributeValueId] || {}),
                endDate: null,
                noEndDate: false,
              },
            }));
          }
          return updated;
        }
        return prevValues;
      });
    },
    [applyEndDateToAll]
  );

  const handleStartDateCellChange = useCallback(
    (params) => {
      const newValue = params.newValue || "";
      const qualityAttributeValueId = params.data.qualityAttributeValueId;
      setSelectedQualityAttributeValues((prevValues) => {
        const updated = [...prevValues];
        const index = updated.findIndex(
          (item) => item.qualityAttributeValueId === qualityAttributeValueId
        );

        if (index > -1) {
          if (applyStartDateToAll) {
            // Apply to all
            updated.forEach((item) => {
              item.startDate = newValue;
            });
            setGlobalStartDate(newValue);
          } else {
            // Apply to single row
            updated[index].startDate = newValue;
            // Update original values map when editing individual cell (apply to all is off)
            setOriginalValues((prevValues) => ({
              ...prevValues,
              [qualityAttributeValueId]: {
                ...(prevValues[qualityAttributeValueId] || {}),
                startDate: newValue,
              },
            }));
          }
          return updated;
        }
        return prevValues;
      });
    },
    [applyStartDateToAll]
  );

  const handleEndDateCellChange = useCallback(
    (params) => {
      const newValue = params.newValue || null;
      const qualityAttributeValueId = params.data.qualityAttributeValueId;

      // Check if "No End Date" is checked - need to read current state
      setNoEndDateFlags((prevFlags) => {
        if (prevFlags[qualityAttributeValueId]) {
          // Revert the change
          params.api.setValue(params.node, params.column, params.oldValue);
          return prevFlags;
        }

        // Update selected values
        setSelectedQualityAttributeValues((prevValues) => {
          const updated = [...prevValues];
          const index = updated.findIndex(
            (item) => item.qualityAttributeValueId === qualityAttributeValueId
          );

          if (index > -1) {
            if (applyEndDateToAll) {
              // Apply to all (only if not disabled by "No End Date")
              updated.forEach((item) => {
                if (!prevFlags[item.qualityAttributeValueId]) {
                  item.endDate = newValue;
                }
              });
              setGlobalEndDate(newValue);
            } else {
              // Apply to single row
              updated[index].endDate = newValue;
              // Update original values map when editing individual cell (apply to all is off)
              setOriginalValues((prevValues) => ({
                ...prevValues,
                [qualityAttributeValueId]: {
                  ...(prevValues[qualityAttributeValueId] || {}),
                  endDate: newValue,
                },
              }));
            }
            return updated;
          }
          return prevValues;
        });

        return prevFlags;
      });
    },
    [applyEndDateToAll]
  );

  const handleApplyToAllChange = useCallback(
    (field, checked) => {
      if (checked) {
        // When checked, save current values to original values map, then apply global value to all
        setSelectedQualityAttributeValues((prevValues) => {
          // Save current values to original values map before applying global value
          const newOriginalValues = {};
          prevValues.forEach((item) => {
            newOriginalValues[item.qualityAttributeValueId] = {
              startDate:
                field === FIELD_START_DATE
                  ? item.startDate || ""
                  : originalValues[item.qualityAttributeValueId]?.startDate ||
                    item.startDate ||
                    "",
              endDate:
                field === FIELD_END_DATE
                  ? item.endDate || null
                  : originalValues[item.qualityAttributeValueId]?.endDate ||
                    item.endDate ||
                    null,
              noEndDate:
                field === FIELD_END_DATE
                  ? stateRef.current.noEndDateFlags[
                      item.qualityAttributeValueId
                    ] || false
                  : originalValues[item.qualityAttributeValueId]?.noEndDate ||
                    false,
            };
          });

          setOriginalValues((prevValues) => ({
            ...prevValues,
            ...newOriginalValues,
          }));

          // Apply global value to all
          const updated = prevValues.map((item) => ({
            ...item,
            [field]:
              field === FIELD_START_DATE
                ? globalStartDate
                : globalNoEndDate
                ? null
                : globalEndDate || null,
          }));
          return updated;
        });

        // For endDate, also apply global "No End Date" flag to all items
        if (field === FIELD_END_DATE) {
          setNoEndDateFlags((prevFlags) => {
            const updatedFlags = { ...prevFlags };
            stateRef.current.selectedQualityAttributeValues.forEach((item) => {
              updatedFlags[item.qualityAttributeValueId] = globalNoEndDate;
            });
            return updatedFlags;
          });
        }

        if (field === FIELD_START_DATE) {
          setApplyStartDateToAll(true);
        } else {
          setApplyEndDateToAll(true);
        }
      } else {
        // When unchecked, restore original values from consolidated map
        if (field === FIELD_END_DATE) {
          // For endDate, restore both the date and the "No End Date" flags together
          // First, restore the "No End Date" flags
          setNoEndDateFlags((prevFlags) => {
            const updatedFlags = { ...prevFlags };
            stateRef.current.selectedQualityAttributeValues.forEach((item) => {
              const originalValue =
                originalValues[item.qualityAttributeValueId];
              if (originalValue?.noEndDate !== undefined) {
                updatedFlags[item.qualityAttributeValueId] =
                  originalValue.noEndDate;
              }
            });
            return updatedFlags;
          });

          // Then, restore the endDate values
          setSelectedQualityAttributeValues((prevValues) => {
            const updated = prevValues.map((item) => {
              const originalValue =
                originalValues[item.qualityAttributeValueId];

              // Restore the endDate: if "No End Date" was true, use null; otherwise use original date
              return {
                ...item,
                endDate:
                  originalValue?.noEndDate === true
                    ? null
                    : originalValue?.endDate !== undefined
                    ? originalValue.endDate
                    : null,
              };
            });
            return updated;
          });
        } else {
          // For startDate, restore normally
          setSelectedQualityAttributeValues((prevValues) => {
            const updated = prevValues.map((item) => {
              const originalValue =
                originalValues[item.qualityAttributeValueId];

              return {
                ...item,
                startDate:
                  originalValue?.startDate !== undefined
                    ? originalValue.startDate
                    : "",
              };
            });
            return updated;
          });
        }

        if (field === FIELD_START_DATE) {
          setApplyStartDateToAll(false);
        } else {
          setApplyEndDateToAll(false);
        }
      }
    },
    [globalStartDate, globalEndDate, globalNoEndDate, originalValues]
  );

  const handleGlobalDateChange = useCallback(
    (field, value) => {
      if (field === FIELD_START_DATE) {
        setGlobalStartDate(value);
        if (applyStartDateToAll) {
          setSelectedQualityAttributeValues((prevValues) =>
            prevValues.map((item) => ({
              ...item,
              [field]: value || null,
            }))
          );
        }
      } else if (field === FIELD_END_DATE) {
        setGlobalEndDate(value);
        if (applyEndDateToAll) {
          setSelectedQualityAttributeValues((prevValues) =>
            prevValues.map((item) => ({
              ...item,
              endDate: globalNoEndDate ? null : value || null,
            }))
          );
        }
      }
    },
    [applyStartDateToAll, applyEndDateToAll, globalNoEndDate]
  );

  const handleGlobalNoEndDateChange = useCallback(
    (checked) => {
      setGlobalNoEndDate(checked);

      if (applyEndDateToAll) {
        setSelectedQualityAttributeValues((prevValues) =>
          prevValues.map((item) => ({
            ...item,
            endDate: checked ? null : globalEndDate || null,
          }))
        );
        // Also update "No End Date" flags for all items
        setNoEndDateFlags((prevFlags) => {
          const updatedFlags = { ...prevFlags };
          stateRef.current.selectedQualityAttributeValues.forEach((item) => {
            updatedFlags[item.qualityAttributeValueId] = checked;
          });
          return updatedFlags;
        });
      }
    },
    [applyEndDateToAll, globalEndDate]
  );

  const handleSave = useCallback(async () => {
    // Validate
    const invalidItems = selectedQualityAttributeValues.filter(
      (item) => !item.startDate
    );

    if (invalidItems.length > 0) {
      toast.error(t("StartDateRequired"));
      return;
    }

    // Validate date ranges
    const invalidRanges = selectedQualityAttributeValues.filter(
      (item) => item.startDate && item.endDate && item.startDate > item.endDate
    );

    if (invalidRanges.length > 0) {
      toast.error(t("StartDateMustBeBeforeEndDate"));
      return;
    }

    try {
      // Prepare assignments for API
      // Convert date strings (YYYY-MM-DD) to ISO date strings for API
      const assignments = selectedQualityAttributeValues.map((item) => ({
        qualityAttributeValueId: item.qualityAttributeValueId,
        marketId: marketId,
        startDate: formatDateToISOString(item.startDate),
        endDate: formatDateToISOString(item.endDate),
        eventId: uuid(),
      }));

      const result = await addQualityAttributeValueMarketAssignments(
        assignments
      );

      // Map qualityAttributeValue from selectedQualityAttributeValues to result
      // The API response may not include qualityAttributeValue, so we preserve it from the input
      const assignmentsWithQualityAttributeValue = (result || assignments).map(
        (assignment) => {
          const selectedItem = selectedQualityAttributeValues.find(
            (item) =>
              item.qualityAttributeValueId ===
              assignment.qualityAttributeValueId
          );
          return {
            ...assignment,
            qualityAttributeValue:
              selectedItem?.qualityAttributeValue ||
              assignment.qualityAttributeValue ||
              "",
          };
        }
      );

      if (submitCallbackFunction) {
        submitCallbackFunction(assignmentsWithQualityAttributeValue);
      }

      toast.success(t("AssignmentsAdded"));

      if (onClose) {
        onClose();
      }
    } catch (error) {
      console.error("Error adding assignments:", error);
      toast.error(
        t("ErrorSaving") + " " + (error.response?.data || error.message)
      );
    }
  }, [
    t,
    marketId,
    selectedQualityAttributeValues,
    submitCallbackFunction,
    onClose,
  ]);

  const mapToOptionsViewModel = useCallback((qualityAttributeValues) => {
    return qualityAttributeValues.map((value) => ({
      value: value.qualityAttributeValueId,
      label: value.value,
    }));
  }, []);

  const optionsViewModel = useMemo(
    () => mapToOptionsViewModel(availableQualityAttributeValues),
    [mapToOptionsViewModel, availableQualityAttributeValues]
  );

  if (isLoading) return <Loading />;

  // Update mode - use separate component
  if (!isNew) {
    return (
      <MarketQualityAttributeAssignmentInputHandler
        assignment={assignment}
        marketId={marketId}
        allowEdit={allowEdit}
        submitCallbackFunction={submitCallbackFunction}
        onClose={onClose}
      />
    );
  }

  // Show dual-list selector with grid below when items selected
  return (
    <div>
      <DualListBox
        canFilter
        className="rdl-hide-move-all"
        disabled={!allowEdit}
        options={optionsViewModel}
        selected={selectedQualityAttributeValueIds}
        onChange={handleDualListBoxChange}
      />
      {selectedQualityAttributeValues.length > 0 && (
        <div className="mt-3">
          <MarketQualityAttributeAssignmentInputGrid
            selectedValues={selectedQualityAttributeValues}
            allowEdit={allowEdit}
            applyStartDateToAll={applyStartDateToAll}
            applyEndDateToAll={applyEndDateToAll}
            globalStartDate={globalStartDate}
            globalEndDate={globalEndDate}
            globalNoEndDate={globalNoEndDate}
            noEndDateFlags={noEndDateFlags}
            onApplyToAllChange={handleApplyToAllChange}
            onGlobalDateChange={handleGlobalDateChange}
            onGlobalNoEndDateChange={handleGlobalNoEndDateChange}
            onStartDateCellChange={handleStartDateCellChange}
            onEndDateCellChange={handleEndDateCellChange}
            onNoEndDateChange={handleNoEndDateChange}
            onClearEndDate={handleClearEndDate}
            onSave={handleSave}
          />
        </div>
      )}
    </div>
  );
};

export default MarketQualityAttributeAssignmentInput;
